import { LoadingSpinner } from "@drs-ops/ui/feedback";
import { useMutation } from "@tanstack/react-query";
import { Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useCanEstimate } from "@/auth/useIsAdmin";
import { useTripPlannerApi } from "@/hooks/useTripPlannerApi";
import type { TripPlannerRequest, TripPlannerResponse } from "@/types/tripPlanner";
import { ResultsSection } from "./ResultsSection";
import { defaultRequest, WhereSection } from "./WhereSection";

const HOME_OFFICE_LABEL = "Home Office — Bloomington, IN";

export function TripPlannerPage(): React.ReactElement {
  const [request, setRequest] = useState<TripPlannerRequest>(defaultRequest);
  const [result, setResult] = useState<TripPlannerResponse | null>(null);
  const { calculate } = useTripPlannerApi();
  const canEstimate = useCanEstimate();

  const mutation = useMutation({
    mutationFn: (body: TripPlannerRequest) => calculate(body),
    onSuccess: (data) => setResult(data),
  });

  if (!canEstimate) return <Navigate to="/" />;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="mb-4 text-2xl font-semibold">Trip Planner</h1>
      <div className="flex flex-col gap-6">
        <WhereSection
          value={request}
          onChange={setRequest}
          onSubmit={() => mutation.mutate(request)}
          submitting={mutation.isPending}
          homeOfficeLabel={HOME_OFFICE_LABEL}
        />
        {mutation.isError && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
            {(mutation.error as Error).message}
          </div>
        )}
        {mutation.isPending && (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        )}
        {result && <ResultsSection data={result} />}
      </div>
    </div>
  );
}
